import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import io from 'socket.io-client';
import axios from 'axios';
import WordCloud from './WordCloud';

const DanmuPage = () => {
  const [danmus, setDanmus] = useState([]);
  const [content, setContent] = useState('');
  const [questions, setQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState('');
  const [showWordCloud, setShowWordCloud] = useState(false);
  const [wordCloudData, setWordCloudData] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const socket = useRef();

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user) {
      navigate('/join');
      return;
    }

    // 根据环境选择Socket.io连接地址
    const socketUrl = process.env.NODE_ENV === 'production' 
      ? window.location.origin 
      : 'http://localhost:5001';
    
    socket.current = io(socketUrl);

    socket.current.on('newDanmu', (newDanmu) => {
      setDanmus(prev => [...prev, newDanmu]);
      
      // 10秒后移除弹幕
      setTimeout(() => {
        setDanmus(prev => prev.filter(d => d.id !== newDanmu.id));
      }, 10000);
    });

    // 获取题目列表
    fetchQuestions();

    // 启动循环播放历史弹幕
    startHistoryLoop();

    return () => {
      if (socket.current) {
        socket.current.disconnect();
      }
    };
  }, [navigate]);

  // 循环播放历史弹幕
  const startHistoryLoop = async () => {
    try {
      const response = await axios.get('/api/history-danmus?limit=30');
      const historyDanmus = response.data;
      
      if (historyDanmus.length > 0) {
        let currentIndex = 0;
        
        const playHistoryDanmu = () => {
          if (danmus.length < 5) { // 当当前弹幕少于5条时播放历史弹幕
            const historyDanmu = historyDanmus[currentIndex];
            const danmuWithId = {
              ...historyDanmu,
              id: `history-${Date.now()}-${Math.random()}`,
              isHistory: true
            };
            
            setDanmus(prev => [...prev, danmuWithId]);
            
            // 10秒后移除
            setTimeout(() => {
              setDanmus(prev => prev.filter(d => d.id !== danmuWithId.id));
            }, 10000);
            
            currentIndex = (currentIndex + 1) % historyDanmus.length;
          }
        };
        
        // 每3秒检查一次是否需要播放历史弹幕
        setInterval(playHistoryDanmu, 3000);
      }
    } catch (error) {
      console.error('获取历史弹幕失败:', error);
    }
  };

  const fetchQuestions = async () => {
    try {
      const response = await axios.get('/api/questions');
      setQuestions(response.data);
      if (response.data.length > 0) {
        setSelectedQuestion(response.data[0].id);
      }
    } catch (error) {
      console.error('获取题目失败:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;

    const user = JSON.parse(localStorage.getItem('user'));
    const formData = new FormData();
    formData.append('userId', user.id);
    formData.append('questionId', selectedQuestion);
    formData.append('content', content);
    formData.append('type', 'text');

    try {
      const response = await axios.post('/api/danmu', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setContent('');
      
      // 检查是否有警告信息
      if (response.data.warning) {
        console.warn(response.data.warning);
      }
      
    } catch (error) {
      console.error('发送弹幕失败:', error);
      
      // 更详细的错误处理
      if (error.response) {
        // 服务器返回了错误状态码
        const errorMsg = error.response.data?.error || '发送失败';
        alert(`发送失败: ${errorMsg}`);
      } else if (error.request) {
        // 网络错误
        alert('网络连接失败，请检查网络后重试');
      } else {
        // 其他错误
        alert('发送失败，请重试');
      }
    }
  };

  const fetchWordCloud = async () => {
    try {
      const response = await axios.get('/api/wordcloud');
      setWordCloudData(response.data);
      setShowWordCloud(true);
    } catch (error) {
      console.error('获取词云数据失败:', error);
      alert('获取词云数据失败，请重试');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    navigate('/join');
  };

  const user = JSON.parse(localStorage.getItem('user'));
  const isTeacher = user?.isTeacher || false;

  return (
    <div className="danmu-page">
      <div className="header">
        <h1>弹幕系统 {isTeacher && <span style={{fontSize: '0.8em', marginLeft: '10px'}}>(教师模式)</span>}</h1>
        <div className="user-info">
          <span>欢迎, {user?.name}</span>
          {isTeacher && (
            <button onClick={fetchWordCloud} className="wordcloud-btn">
              词云统计
            </button>
          )}
          <button onClick={handleLogout} className="logout-btn">退出</button>
        </div>
      </div>

      <div className="danmu-container">
        {danmus.map((danmu) => (
          <div key={danmu.id} className={`danmu ${danmu.type} ${danmu.isHistory ? 'history' : ''}`}>
            {danmu.type === 'image' ? (
              <img src={danmu.imageUrl} alt="弹幕图片" />
            ) : (
              <span>{danmu.content}</span>
            )}
          </div>
        ))}
      </div>

      {!isTeacher && (
      <div className="input-section">
        <form onSubmit={handleSubmit}>
          <div className="question-select">
            <label>选择题目:</label>
            <select 
              value={selectedQuestion} 
              onChange={(e) => setSelectedQuestion(e.target.value)}
            >
              {questions.map(q => (
                <option key={q.id} value={q.id}>{q.title}</option>
              ))}
            </select>
          </div>

          <div className="input-group">
            <input
              type="text"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="输入弹幕内容..."
              className="text-input"
            />
            <button type="submit" className="send-btn">发送</button>
          </div>
        </form>
      </div>
      )}

      {/* 词云显示区域 */}
      {showWordCloud && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            maxWidth: '95vw',
            maxHeight: '95vh',
            overflow: 'auto',
            boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
          }}>
            <WordCloud 
              data={wordCloudData} 
              onClose={() => setShowWordCloud(false)} 
            />
          </div>
        </div>
      )}

      <style jsx>{`
        .danmu-page {
          height: 100vh;
          display: flex;
          flex-direction: column;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 2rem;
          background: rgba(0, 0, 0, 0.2);
        }

        .user-info {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .logout-btn {
          padding: 0.5rem 1rem;
          background: rgba(255, 255, 255, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.3);
          color: white;
          border-radius: 4px;
          cursor: pointer;
        }

        .wordcloud-btn {
          padding: 0.5rem 1rem;
          background: rgba(255, 193, 7, 0.9);
          border: 1px solid rgba(255, 193, 7, 0.7);
          color: white;
          border-radius: 4px;
          cursor: pointer;
          margin-right: 10px;
          font-weight: 500;
          transition: all 0.3s ease;
        }

        .wordcloud-btn:hover {
          background: rgba(255, 193, 7, 1);
          border-color: rgba(255, 193, 7, 1);
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(255, 193, 7, 0.3);
        }

        .danmu-container {
          flex: 1;
          position: relative;
          overflow: hidden;
          padding: 1rem;
        }

        .danmu {
          position: absolute;
          background: rgba(0, 0, 0, 0.7);
          padding: 0.5rem 1rem;
          border-radius: 20px;
          color: white;
          font-size: 1.1rem;
          white-space: nowrap;
          animation: danmuMove 10s linear;
        }

        .danmu.history {
          background: rgba(0, 0, 0, 0.5);
          border: 1px solid rgba(255, 255, 255, 0.3);
          opacity: 0.8;
        }

        .danmu.image img {
          max-width: 200px;
          max-height: 150px;
          border-radius: 8px;
        }

        @keyframes danmuMove {
          from {
            left: 100%;
            transform: translateX(0);
          }
          to {
            left: -100%;
            transform: translateX(-100%);
          }
        }

        .input-section {
          padding: 1rem 2rem;
          background: rgba(0, 0, 0, 0.2);
        }

        .question-select {
          margin-bottom: 1rem;
        }

        .question-select label {
          margin-right: 0.5rem;
        }

        .question-select select {
          padding: 0.5rem;
          border-radius: 4px;
          border: none;
          background: white;
        }

        .input-group {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }

        .text-input {
          flex: 1;
          padding: 0.75rem;
          border: none;
          border-radius: 4px;
          font-size: 1rem;
          min-height: 44px;
          box-sizing: border-box;
        }

        .send-btn {
          padding: 0.75rem 1.5rem;
          background: #4CAF50;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 1rem;
          min-width: 80px;
          min-height: 44px;
          white-space: nowrap;
        }

        /* 移动端优化 */
        @media (max-width: 768px) {
          .input-group {
            gap: 0.3rem;
          }
          
          .text-input {
            font-size: 16px; /* 防止iOS缩放 */
            min-height: 48px;
          }
          
          .send-btn {
            padding: 0.8rem 1.2rem;
            font-size: 1rem;
            min-width: 70px;
            min-height: 48px;
            font-weight: bold;
          }
        }

        .send-btn:hover {
          background: #45a049;
        }
      `}</style>
    </div>
  );
};

export default DanmuPage;